import React from "react";
import { useState } from "react";
import { useEffect, useRef } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Typography } from "@mui/material";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import {
  createPetInsurance,
  getpetinsurancebyid,
  getProviderNamesByWorkspaceId,
  getSubscriberNamesByWorkspaceId,
  Updatepetinsuance,
} from "../../Api/Services/PetInsuranceServices";
import { getPetNameByWorkspaceId } from "../../Api/Services/PetServices";
import { getUsersByWorkspaceId } from "../../Api/Services/UserServices";
import useAuth from '../../ContextProvider/AuthProvider'
import { FormHeaderBox, FormHeaderTitle, Body, Column, ButtonBoxStyle, CancelButton } from "../../Css";
import { PetInsuranceFormValidation } from "../../From/Validation";
import { DurationOptions } from "../../Options";
import { formatDate } from "../../Utils";
import AutoComplete from "../Common/AutoComplete";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import FormLayout from "../Common/FormHeader";
import InputField from "../Common/InputField";
import CustomPhoneInput from "../Common/PhoneInput";
import  { showSnackbar } from "../Common/Snackbar";
import "../pet-information/petPrint.css";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  plan: "",
  member_id: "",
  member_name: "",
  effective_date: "",
  duration: "",
  group_name: "",
  pet_id: "",
  provider_name: "",
  provider_phone: "",
  subscriber_name: "",
};

const Petinsurance = () => {
  const { workspaceId: w_id } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const componentPDF = useRef();

  // Determine if this is edit mode
  const isEditMode = !!id;

  const [initialValue, setInitialValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [providerNames, setProviderNames] = useState([]);
  const [subscriberNames, setSubscriberNames] = useState([]);
  const [memberNames, setMemberNames] = useState([]);

  // Load existing data for edit mode
  const loadPetInsuranceDetails = async () => {
    if (!isEditMode) return;
    
    try {
      const { data } = await getpetinsurancebyid(id);
      setInitialValue({
        ...initialValue,
        plan: data?.data?.plan,
        member_id: data?.data?.member_id,
        member_name: data?.data?.member_name,
        effective_date: formatDate(data?.data?.effective_date),
        duration: data?.data?.duration,
        group_name: data?.data?.group_name,
        pet_id: data?.data?.pet_id,
        provider_name: data?.data?.provider_name,
        provider_phone: data?.data?.provider_phone,
        subscriber_name: data?.data?.subscriber_name,
      });
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to load pet insurance details.",
      });
    }
  };

  const getPetNames = async () => {
    try {
      const { data } = await getPetNameByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item?.id,
          label: item?.name,
        };
      });
      setItems(filteredData);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to fetch pet names",
      });
    }
  };

  const getProviderNames = async () => {
    try {
      const { data } = await getProviderNamesByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item,
          label: item,
        };
      });
      filteredData.push({ value: "Type here", label: "Type here" });
      setProviderNames(filteredData);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to fetch provider names",
      });
    }
  };

  const getSubscriberNames = async () => {
    try {
      const { data } = await getSubscriberNamesByWorkspaceId(w_id);
      const filteredData = data?.data?.map((item) => {
        return {
          value: item,
          label: item,
        };
      });
      filteredData.push({ value: "Type here", label: "Type here" });
      setSubscriberNames(filteredData);
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to fetch subscriber names",
      });
    }
  };

  const getUsersByWorkspaceID = async () => {
    try {
      const res = await getUsersByWorkspaceId(w_id);
      const filteredData = res?.data?.data?.map((item) => {
        return {
          value: `${item?.first_name} ${item?.last_name}`,
          label: `${item?.first_name} ${item?.last_name}`,
        };
      });
      if (filteredData?.length === 0) {
        <></>;
      } else {
        setMemberNames(filteredData);
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Failed to fetch user details",
      });
    }
  };

  useEffect(() => {
    getPetNames();
    getProviderNames();
    getSubscriberNames();
    getUsersByWorkspaceID();
    if (isEditMode) {
      loadPetInsuranceDetails();
    }
  }, [w_id, isEditMode]);

  // Create new pet insurance
  const createPetInsuranceData = async (values) => {
    const data = {
      plan: values?.plan,
      member_id: values?.member_id,
      member_name: values?.member_name,
      start_date: values?.effective_date,
      effective_date: values?.effective_date,
      duration: values?.duration,
      group_name: values?.group_name,
      w_id: w_id,
      pet_id: values?.pet_id,
      provider_name: values?.provider_name,
      provider_phone: values?.provider_phone,
      subscriber_name: values?.subscriber_name,
    };
    
    try {
      await createPetInsurance(data);
      showSnackbar({
        type: "success",
        message: "Pet insurance added successfully.",
      });
    } catch (err) {
      showSnackbar({
        type: "error",
        message: "Pet insurance add failed.",
      });
      throw err;
    }
  };

  // Update existing pet insurance
  const updatePetInsuranceData = async (values) => {
    try {
      const res = await Updatepetinsuance(id, values);
      if (res?.data?.success) {
        showSnackbar({
          type: "success",
          message: "Pet insurance updated successfully.",
        });
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Pet insurance update failed.",
      });
      throw error;
    }
  };

  const handleSubmit = async (values, { isSubmitting }) => {
    try {
      if (isEditMode) {
        await updatePetInsuranceData(values);
      } else {
        await createPetInsuranceData(values);
      }
    } catch (error) {
      showSnackbar({
        type: "error",
        message: "Pet insurance add failed.",
      });
    } finally {
      isSubmitting(false);
    }
  };

  // Handle print functionality
  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Pet Insurance",
    pageStyle: `
      @page { size: A3; margin: 200px; }
      body { margin: 100px; }
      .print-text-field { font-size: 14px; }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Pet Insurance Information" : "Pet Insurance Information"}>
        <Formik
          initialValues={initialValue}
          onSubmit={handleSubmit}
          validationSchema={PetInsuranceFormValidation}
          enableReinitialize
        >
          {({ getFieldProps, values, isSubmitting, setFieldValue }) => (
            <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
            <Box sx={{ borderBottom: `1px solid ${theme.palette.primary.main}`, width: "100%" }}>
                <Typography variant="h6" sx={FormHeaderTitle}>
                Pet Insurance Details
                </Typography>
              </Box>
              <div ref={componentPDF} style={{ width: "100%" }}>
                <Body className="scroll2" style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_id" label={"Pet Name"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_id")}
                        options={items}
                        value={isEditMode ? items.find(option => option.value === values.pet_id) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="plan" label={"Plan"} requiredField>
                      <InputField {...getFieldProps("plan")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="provider_name"
                      label={"Insurance Provider Name"}
                      requiredField
                    >
                      <AutoComplete
                        {...getFieldProps("provider_name")}
                        options={providerNames}
                        otherType="Type here"
                        placeholder="Select a Provider Name"
                        value={isEditMode ? providerNames.find(option => option.value === values.provider_name) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="group_name" label={"Group"} requiredField>
                      <InputField {...getFieldProps("group_name")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="member_name"
                      label={"Member Name"}
                      requiredField
                    >
                      <AutoComplete
                        freeSolo={!isEditMode}
                        {...getFieldProps("member_name")}
                        options={memberNames}
                        otherType="Type here"
                        placeholder="Select a Member Name"
                        onInputChange={!isEditMode ? (e, value) =>
                          setFieldValue("member_name", value)
                        : undefined}
                        value={isEditMode ? memberNames.find(option => option.value === values.member_name) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="effective_date"
                      label={"Effective Date"}
                      requiredField
                    >
                      <InputField
                        type="date"
                        {...getFieldProps("effective_date")}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="member_id" label={"Insurance Member ID"} requiredField>
                      <InputField {...getFieldProps("member_id")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="subscriber_name"
                      label={"Subscriber Name"}
                      requiredField
                    >
                      <AutoComplete
                        {...getFieldProps("subscriber_name")}
                        options={subscriberNames}
                        otherType="Type here"
                        placeholder="Select a Subscriber Name"
                        value={isEditMode ? subscriberNames.find(option => option.value === values.subscriber_name) : undefined}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="provider_phone"
                      label={"Phone Number"}
                      requiredField
                    >
                      <CustomPhoneInput
                        {...getFieldProps("provider_phone")}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="duration" label={"Duration"} requiredField>
                      <AutoComplete
                        {...getFieldProps("duration")}
                        options={DurationOptions}
                        value={isEditMode ? DurationOptions.find(option => option.value === values.duration) : undefined}
                      />
                    </FormGroup>
                  </Column>
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting}
                    type="submit"
                    />
                  {isEditMode && (
                    <CustomButton
                      startIcon={<LocalPrintshopOutlinedIcon />}
                      onClick={handlePrint}
                    />
                  )}
                </Box>
              </Box>
                    </Box>
            </Form>
          )}
        </Formik>
    </FormLayout>
  );
};

export default Petinsurance;
